(() => {
  if (window.GiestoProductReminder) return;
  window.GiestoProductReminder = true;
  const instances = new Set();
  let toastTimer;
  function clearToast() {
    clearTimeout(toastTimer);
    const toast = document.querySelector('[data-reminder-toast]');
    if (toast) toast.hidden = true;
  }
  function showToast(message, error = false) {
    let toast = document.querySelector('[data-reminder-toast]');
    if (!toast) {
      toast = document.createElement('aside');
      toast.className = 'giesto-reminder-toast';
      toast.dataset.reminderToast = '';
      toast.setAttribute('aria-atomic', 'true');
      toast.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="currentColor"/><path d="m6.8 12.3 3.2 3.2 7.2-7.2" fill="none" stroke="#fff" stroke-width="1.8"/></svg><span></span>';
      document.body.append(toast);
    }
    clearTimeout(toastTimer);
    toast.setAttribute('role', error ? 'alert' : 'status');
    toast.setAttribute('aria-live', error ? 'assertive' : 'polite');
    toast.dataset.error = String(error);
    toast.querySelector('path').setAttribute('d', error ? 'm8 8 8 8m0-8-8 8' : 'm6.8 12.3 3.2 3.2 7.2-7.2');
    toast.hidden = false;
    toast.querySelector('span').textContent = message;
    toastTimer = setTimeout(() => { toast.hidden = true; }, error ? 7000 : 5000);
  }
  function initialize(box) {
    if (box.dataset.reminderReady) return;
    box.dataset.reminderReady = 'true';
    const copy = Object.fromEntries(['idle', 'saved', 'canceled', 'saving', 'error'].map(key => [key, box.getAttribute(`data-reminder-${key}`)]));
    copy.ready = box.dataset.reminderReadyMessage;
    const button = box.querySelector('[data-reminder-toggle]');
    const label = button.querySelector('[data-reminder-label]');
    const form = box.querySelector('form');
    const key = `giesto:reminder:${box.dataset.productId}`;
    let receipt = '';
    try { receipt = localStorage.getItem(key) || ''; } catch { /* Storage is optional. */ }
    if (!/^[A-Za-z0-9_-]{43}$/.test(receipt)) {
      receipt = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32)))).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
      try { localStorage.setItem(key, receipt); } catch { /* Account state stays on the server. */ }
    }
    let busy = false;
    let connected = false;
    let state = 'none';
    let revision = 0;
    function paint() {
      const enrolled = state !== 'none';
      button.setAttribute('aria-pressed', String(enrolled));
      button.disabled = !connected || busy || enrolled;
      button.setAttribute('aria-busy', String(busy));
      label.textContent = !connected ? box.dataset.reminderUnavailable : busy ? copy.saving : enrolled ? (state === 'canceled' ? copy.canceled : copy.saved) : copy.idle;
      button.querySelector('svg').toggleAttribute('hidden', !connected);
      form.querySelector('button').disabled = busy;
      if (enrolled) { form.hidden = true; button.setAttribute('aria-expanded', 'false'); }
    }
    function endpoint(path) {
      const configured = box.dataset.proxyPath;
      if (!/^\/(apps|a|community|tools)\/[a-z0-9_-]+\/?$/i.test(configured || '')) throw new Error('Invalid proxy path');
      // The local theme has no deployed Shopify app proxy. Its separate,
      // loopback-only service saves guest previews with sending hard-disabled.
      const origin = ['localhost', '127.0.0.1'].includes(location.hostname)
        ? `http://127.0.0.1:8790` : location.origin;
      return new URL(`${configured.replace(/\/$/, '')}/${path}`, origin);
    }
    async function request(path, input) {
      const url = endpoint(path);
      if (!input) { url.searchParams.set('productId', box.dataset.productId); url.searchParams.set('receipt', receipt); }
      const response = await fetch(url, { method: input ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        signal: AbortSignal.timeout(12000), ...(input ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) } : {}) });
      if (!response.ok) throw new Error('Reminder request failed');
      const data = await response.json();
      if (typeof data.enrolled !== 'boolean' || !['none', 'pending', 'confirmed', 'notified', 'canceled'].includes(data.state)) throw new Error('Invalid response');
      return data;
    }
    async function sync() {
      if (busy || !box.isConnected) return;
      const current = ++revision;
      try {
        const data = await request('status');
        if (current !== revision || busy) return;
        connected = true;
        state = data.state;
        paint();
      } catch { /* Failed refresh never invents a saved enrollment. */ }
    }
    async function save(email) {
      const result = await request('remind', { productId: box.dataset.productId, handle: box.dataset.productHandle,
        consent: true, receipt, ...(email ? { email } : {}), website: form.elements.website.value });
      if (!result.enrolled) throw new Error('Not saved');
      state = result.state;
      showToast(state === 'canceled' ? copy.canceled : copy.ready);
      form.elements.email.value = '';
      instances.forEach(item => { if (item.box !== box) void item.sync(); });
      try { localStorage.setItem(`${key}:updated`, String(Date.now())); } catch { /* Other tabs can refresh on focus. */ }
    }
    button.addEventListener('click', async () => {
      if (busy || state !== 'none') return;
      busy = true; ++revision; clearToast(); paint();
      try {
        // Recheck Shopify identity on every interaction, including account switches.
        const data = await request('status'); state = data.state;
        if (!data.enrolled) {
          if (data.authenticated) await save();
          else { form.hidden = false; button.setAttribute('aria-expanded', 'true'); form.elements.email.focus(); }
        }
      } catch { showToast(copy.error, true); }
      finally { busy = false; paint(); }
    });
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (busy || state !== 'none' || !form.reportValidity()) return;
      busy = true; ++revision; clearToast(); paint();
      try { await save(form.elements.email.value); }
      catch { showToast(copy.error, true); }
      finally { busy = false; paint(); }
    });
    instances.add({ box, sync });
    void sync();
  }
  function scan() {
    for (const item of instances) if (!item.box.isConnected) instances.delete(item);
    document.querySelectorAll('.giesto-product-reminder').forEach(initialize);
  }
  function refresh() { scan(); instances.forEach(item => { void item.sync(); }); }
  document.addEventListener('shopify:section:load', scan);
  window.addEventListener('pageshow', refresh);
  window.addEventListener('focus', refresh);
  window.addEventListener('storage', event => { if (event.key?.startsWith('giesto:reminder:')) refresh(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  scan();
})();
